import { useEffect } from "react";
import { toast } from "sonner";
import { useSettingsState } from "@/context/SettingsStateContext";
import { useWebsites } from "@/context/WebsitesContext";
import { supabase } from "@/integrations/supabase/client";
import { Category } from "@/types/settings.types";
import config from "@/config";
import { useWordPress } from "@/context/WordPressContext";
import useWordpressSettings from "./useWordpressSettings";

export default function useWordpressCategories() {
  const { currentWebsite } = useWebsites();
  const { settings: wpSettings, isLoading: wpLoading } = useWordPress();
  const {
    setShowAuthDialog,
    wpUsername,
    setWpUsername,
    wpPassword,
    setWpPassword,
    setConnectionError,
    setConnectionDiagnostics,
    directWpSettings,
    categories,
    setCategories,
    setShowCategoryDialog,
    generatedCategories,
    setGeneratedCategories,
    setIsGeneratingCategories,
  } = useSettingsState();

  const { handleStartWordPressAuth } = useWordpressSettings();

  // Add function to fetch categories from WordPress
  const fetchCategoriesFromWordPress = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    setIsGeneratingCategories(true);
    try {
      console.log("Fetching categories from WordPress for website:", currentWebsite.id);

      const response = await fetch(
        `${config.supabase.functionsUrl}/wordpress-categories`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (
                await supabase.auth.getSession()
              ).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            websiteId: currentWebsite.id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching categories from WordPress:", error);
        throw new Error(error);
      }

      const data = await response.json();
      console.log("Categories fetched from WordPress:", data);

      // Refresh the local categories list
      await fetchCategories();

      toast.success("Categories refreshed from WordPress");
    } catch (error) {
      console.error("Error fetching categories from WordPress:", error);
      toast.error("Failed to fetch categories from WordPress");
    } finally {
      setIsGeneratingCategories(false);
    }
  };

  // Add function to push categories to WordPress
  const pushCategoriesToWordPress = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    setIsGeneratingCategories(true);
    try {
      console.log("Pushing categories to WordPress for website:", currentWebsite.id);

      // Get all categories for this website
      const { data: categories, error: categoriesError } = await supabase
        .from("wordpress_categories")
        .select("*")
        .eq("website_id", currentWebsite.id);

      if (categoriesError) {
        throw new Error(`Failed to fetch categories: ${categoriesError.message}`);
      }

      if (!categories || categories.length === 0) {
        toast.info("No categories to push to WordPress");
        return;
      }

      // Get current session for authentication
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData?.session?.access_token) {
        toast.error("You must be logged in to push categories");
        return;
      }

      // Push categories using the new edge function
      const response = await fetch(`${config.supabase.functionsUrl}/push-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({
          websiteId: currentWebsite.id,
          categories: categories.map((cat) => ({
            name: cat.name,
            slug: cat.slug,
            description: cat.description || "",
          })),
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        console.error("Error pushing categories to WordPress:", error);
        throw new Error(error);
      }

      const data = await response.json();
      console.log("Push categories response:", data);

      // Check results
      const successful = data.results.filter((r) => r.success).length;
      const failed = data.results.filter((r) => !r.success).length;

      if (failed > 0) {
        toast.warning(`Pushed ${successful} categories, ${failed} failed`);
        console.error(
          "Failed categories:",
          data.results.filter((r) => !r.success)
        );
      } else {
        toast.success(`Successfully pushed ${successful} categories to WordPress`);
      }

      // Refresh the categories list
      await fetchCategories();
    } catch (error) {
      console.error("Error pushing categories to WordPress:", error);
      toast.error("Failed to push categories to WordPress");
    } finally {
      setIsGeneratingCategories(false);
    }
  };

  // Add function to fetch categories
  const fetchCategories = async () => {
    if (!currentWebsite) {
      console.log("No website selected, skipping category fetch");
      return;
    }

    try {
      console.log("Fetching categories for website:", currentWebsite.id);
      const { data: categories, error } = await supabase
        .from("wordpress_categories")
        .select("*")
        .eq("website_id", currentWebsite.id)
        .order("name");

      if (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to fetch categories");
        return;
      }

      console.log("Fetched categories:", categories);
      setCategories(categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  // Improve button click to explicitly log and confirm credentials being loaded
  const openAuthDialogWithCredentials = () => {
    // Clear any previous errors when starting a new connection attempt
    setConnectionError(null);
    setConnectionDiagnostics(null);

    // Use either direct settings or context settings
    const settings = directWpSettings || wpSettings;

    if (settings) {
      console.log("Setting credentials before opening dialog:", {
        username: settings.wp_username,
        passwordLength: settings.wp_application_password?.length || 0,
      });

      // First set credentials, then open dialog
      setWpUsername(settings.wp_username || "");
      setWpPassword(settings.wp_application_password || "");

      // Open dialog
      setShowAuthDialog(true);

      // Double check with timeout to ensure state is updated
      setTimeout(() => {
        if (!wpUsername || !wpPassword) {
          console.log("Credentials not set correctly, retrying with timeout");
          setWpUsername(settings.wp_username || "");
          setWpPassword(settings.wp_application_password || "");
        }
      }, 100);
    } else {
      // For new connections, clear credentials and start WordPress auth
      setWpUsername("");
      setWpPassword("");
      setShowAuthDialog(true);
      handleStartWordPressAuth();
    }
  };

  // Add function to generate category ideas
  const generateCategoryIdeas = async () => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    setIsGeneratingCategories(true);
    try {
      console.log("Generating category ideas for website:", currentWebsite.id);

      // Get existing categories to exclude them
      const existingCategories = categories.map((c) => c.name.toLowerCase());
      console.log("Existing categories:", existingCategories);

      const response = await fetch(
        `${config.supabase.functionsUrl}/generate-category-ideas`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (
                await supabase.auth.getSession()
              ).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            websiteId: currentWebsite.id,
            existingCategories, // Pass existing categories to exclude
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error generating categories:", error);
        throw new Error(error);
      }

      const data = await response.json();
      console.log("Generated categories:", data);

      // Filter out any categories that already exist
      const newCategories = data.categories.filter(
        (category: { name: string; slug: string }) =>
          !existingCategories.includes(category.name.toLowerCase())
      );

      if (newCategories.length === 0) {
        toast.info(
          "No new category ideas generated. All suggested categories already exist."
        );
        return;
      }

      setGeneratedCategories(newCategories);
      setShowCategoryDialog(true);
      toast.success("Category ideas generated successfully");
    } catch (error) {
      console.error("Error generating categories:", error);
      toast.error("Failed to generate category ideas");
    } finally {
      setIsGeneratingCategories(false);
    }
  };

  // Add function to handle adding a category
  const addCategory = async (category: { name: string; slug: string }) => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    try {
      console.log("Adding category:", category);

      // Generate a random ID between 1 and 1000000 for wp_category_id
      const wp_category_id = Math.floor(Math.random() * 1000000) + 1;

      // Insert the category into the database
      const { error } = await supabase.from("wordpress_categories").insert({
        website_id: currentWebsite.id,
        wp_category_id,
        name: category.name,
        slug: category.slug,
        description: "", // Optional description
        parent_id: 0, // No parent category
        count: 0, // No posts yet
      });

      if (error) {
        console.error("Error adding category:", error);
        throw error;
      }

      // Refresh the categories list
      await fetchCategories();

      // Show success message but keep modal open
      toast.success(`Category "${category.name}" added successfully`);

      // Remove the added category from the generated list
      setGeneratedCategories(generatedCategories.filter((c) => c.name !== category.name));

      // If no more categories to add, close the modal
      if (generatedCategories.length === 1) {
        setShowCategoryDialog(false);
      }
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error("Failed to add category");
    }
  };

  /**
   * Renders the WordPress connection additional information when connected
   * Shows last interaction with API without replacing the existing UI
   */
  const renderConnectionStatusInfo = () => {
    if (!wpSettings) return null;

    return (
      <div className="mt-4 border rounded-md p-4 bg-muted/30 space-y-3">
        <h4 className="text-sm font-medium">Connection Status Information</h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Last connection test:</span>
              <span className="text-xs font-medium">
                {directWpSettings?.updated_at || wpSettings?.updated_at
                  ? new Date(
                      directWpSettings?.updated_at || wpSettings?.updated_at
                    ).toLocaleString()
                  : "Unknown"}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">First connected on:</span>
              <span className="text-xs font-medium">
                {directWpSettings?.created_at || wpSettings?.created_at
                  ? new Date(
                      directWpSettings?.created_at || wpSettings?.created_at
                    ).toLocaleString()
                  : "Unknown"}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">API endpoint:</span>
              <code className="text-xs bg-muted p-1 rounded">
                {directWpSettings?.wp_url || wpSettings?.wp_url
                  ? `${directWpSettings?.wp_url || wpSettings?.wp_url}/wp-json/wp/v2/`
                  : "Not configured"}
              </code>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">Authentication:</span>
              <span className="text-xs font-medium">Application Password</span>
            </div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground mt-2">
          <strong>Tip:</strong> If you experience connection issues, check that your
          WordPress REST API is enabled and your site is accessible.
        </div>
      </div>
    );
  };

  const handleRefreshCategories = async () => {
    setIsGeneratingCategories(true);
    try {
      await generateCategoryIdeas();
    } catch (error) {
      console.error("Error refreshing categories:", error);
      toast.error("Failed to refresh categories");
    } finally {
      setIsGeneratingCategories(false);
    }
  };

  // Add function to handle category deletion
  const handleDeleteCategory = async (category: Category) => {
    if (!currentWebsite) {
      toast.error("Please select a website first");
      return;
    }

    try {
      console.log("Deleting category:", category);

      const response = await fetch(
        `${config.supabase.functionsUrl}/manage-local-categories`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              (
                await supabase.auth.getSession()
              ).data.session?.access_token
            }`,
          },
          body: JSON.stringify({
            websiteId: currentWebsite.id,
            categoryId: category.wp_category_id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error deleting category:", error);
        throw new Error(error);
      }

      // Update local state
      setCategories(
        categories.filter((c) => c.wp_category_id !== category.wp_category_id)
      );
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete category");
    }
  };

  // Add useEffect to fetch categories when currentWebsite changes
  useEffect(() => {
    if (currentWebsite) {
      console.log("Current website changed, fetching categories");
      fetchCategories();
    }
  }, [currentWebsite]);

  return {
    fetchCategories,
    fetchCategoriesFromWordPress,
    pushCategoriesToWordPress,
    openAuthDialogWithCredentials,
    generateCategoryIdeas,
    addCategory,
    renderConnectionStatusInfo,
    handleRefreshCategories,
    handleDeleteCategory,
  };
}
